import { useEffect, useRef, useState } from 'react';

/**
 * Hook to manage header visibility based on scroll direction.
 * Useful for "Hide on scroll down, Show on scroll up" behavior.
 * 
 * @param threshold - Minimum scroll distance from top before hiding starts.
 * @param delta - Minimum scroll distance change required to trigger visibility toggle.
 */
export default function useHeaderVisibility(threshold = 100, delta = 10) {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Ensure we always show at the very top
      if (currentScrollY < threshold) {
        setIsVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Delta handling to prevent jitter
      if (Math.abs(currentScrollY - lastScrollY.current) < delta) {
        return;
      }

      // Hide only on scroll down, show on scroll up
      if (currentScrollY > lastScrollY.current) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold, delta]);

  return isVisible;
}
